// Who may see one recruitment flow step: pure functions over the server's people list plus the ticks
// not yet written. Nothing here knows when those ticks are written.

export const GRANT = 'grant';
export const REVOKE = 'revoke';

export const matchesQuery = (text, query) => {
    const needle = (query || '').trim().toLowerCase();
    return needle === '' || String(text || '').toLowerCase().includes(needle);
};

// A locked person sees the step whatever the rules say -- company-wide access, or the position's
// lead -- and the server says which, so the client never infers it from a role label.
export const isChecked = (person, pending = {}) => {
    if (person.locked) {
        return true;
    }
    const intent = pending[person.id];
    if (intent === GRANT) {
        return true;
    }
    if (intent === REVOKE) {
        return false;
    }
    return Boolean(person.has_access);
};

// Ticking back to the server's own state drops the entry, so an undone tick costs no request -- and,
// for someone outside the team, no invitation.
export const toggleAccess = (pending = {}, person) => {
    if (person.locked) {
        return pending;
    }
    const next = { ...pending };
    const wanted = !isChecked(person, pending);
    if (wanted === Boolean(person.has_access)) {
        delete next[person.id];
    } else {
        next[person.id] = wanted ? GRANT : REVOKE;
    }
    return next;
};

export const hasPendingAccess = (pending = {}) => Object.keys(pending).length > 0;

// The server sends each person once with the steps their rules withhold; the tick is per step, so the
// per-step view is derived here -- the rule is stored against the person.
export const peopleForStep = (people = [], stepId) => {
    const key = String(stepId);
    return people.map((person) => ({
        ...person,
        has_access: !(person.withheld_steps || []).map(String).includes(key),
    }));
};

export const accessPayload = (pending = {}) => ({
    grant: Object.keys(pending).filter((id) => pending[id] === GRANT),
    revoke: Object.keys(pending).filter((id) => pending[id] === REVOKE),
});

// "Other people" stays empty until something is typed: the directory is searchable, not browsable.
export const groupPeople = ({ people = [], directory = [], query = '' }) => {
    const onStep = people.filter((person) => matchesQuery(person.text, query));
    const known = new Set(people.map((person) => person.id));
    const searching = (query || '').trim() !== '';
    return {
        team: onStep,
        others: searching
            ? directory.filter((person) => !known.has(person.id) && matchesQuery(person.text, query))
            : [],
    };
};

// Who loses their place on the hiring team if this step goes: deleting it prunes the rules naming it,
// and an emptied rule set means FULL access, so the server ends their participation instead.
export const participationEndedByRemoving = ({ people = [], stepIds = [], stepId }) => {
    const removed = String(stepId);
    const remaining = stepIds.map(String).filter((id) => id !== removed);
    return people.filter((person) => {
        // No list at all means no rules, which means full access; an empty one means rules that
        // withhold nothing today.
        if (person.locked || !Array.isArray(person.withheld_steps)) {
            return false;
        }
        const withheld = new Set(person.withheld_steps.map(String));
        // A step they could not see anyway was not their last one.
        return !withheld.has(removed) && remaining.every((id) => withheld.has(id));
    });
};


